/**
 * Polar coordinate utilities for radial maze support.
 *
 * Provides cartesian↔polar conversion and collision detection
 * for arc walls (concentric rings) and radial walls (spokes).
 */

export interface PolarCoord {
  r: number;
  theta: number; // in radians, [0, 2π)
}

export interface CartesianCoord {
  x: number;
  y: number;
}

/** Arc wall — a segment of a concentric ring at a given radius. */
export interface ArcWall {
  type: 'arc';
  center: CartesianCoord;
  radius: number;
  startAngle: number; // radians
  endAngle: number;   // radians
}

/** Radial wall — a spoke from innerRadius to outerRadius at a fixed angle. */
export interface RadialWall {
  type: 'radial';
  center: CartesianCoord;
  angle: number;       // radians
  innerRadius: number;
  outerRadius: number;
}

export type PolarWall = ArcWall | RadialWall;

const TWO_PI = 2 * Math.PI;

/**
 * Convert cartesian (x, y) to polar (r, θ) coordinates.
 * θ is normalized to [0, 2π). Optional center defaults to origin.
 */
export function cartesianToPolar(
  x: number,
  y: number,
  cx = 0,
  cy = 0,
): PolarCoord {
  const dx = x - cx;
  const dy = y - cy;
  const r = Math.sqrt(dx * dx + dy * dy);
  let theta = Math.atan2(dy, dx);
  if (theta < 0) theta += TWO_PI;
  return { r, theta };
}

/**
 * Convert polar (r, θ) to cartesian (x, y) coordinates.
 * Optional center defaults to origin.
 */
export function polarToCartesian(
  r: number,
  theta: number,
  cx = 0,
  cy = 0,
): CartesianCoord {
  return {
    x: cx + r * Math.cos(theta),
    y: cy + r * Math.sin(theta),
  };
}

/**
 * Check whether a straight-line path from `from` to `to` crosses a polar wall.
 *
 * Supports two wall types:
 * - **arc**: a concentric ring segment — detects when the path crosses the ring
 *   within the wall's angular span.
 * - **radial**: a spoke segment — detects when the path crosses the spoke line
 *   within the wall's radius range.
 *
 * Points exactly on the wall boundary are treated as *not* crossing.
 */
export function isPathCrossingRadialWall(
  from: CartesianCoord,
  to: CartesianCoord,
  wall: PolarWall,
): boolean {
  if (wall.type === 'arc') {
    return isPathCrossingArcWall(from, to, wall);
  }
  return isPathCrossingRadialSpoke(from, to, wall);
}

// ---------------------------------------------------------------------------
// Arc wall collision (concentric ring boundary)
// ---------------------------------------------------------------------------

function isPathCrossingArcWall(
  from: CartesianCoord,
  to: CartesianCoord,
  wall: ArcWall,
): boolean {
  const { center, radius, startAngle, endAngle } = wall;

  // Translate so that center is at origin
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const fx = from.x - center.x;
  const fy = from.y - center.y;

  // Parametric line: P(t) = from + t*(to - from), t ∈ [0,1]
  // |P(t)|² = radius² → solve quadratic in t
  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - radius * radius;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return false;
  if (a === 0) return false; // zero-length segment

  const sqrtD = Math.sqrt(discriminant);
  const eps = 1e-9;

  for (const sign of [-1, 1]) {
    const t = (-b + sign * sqrtD) / (2 * a);
    // Strictly inside the segment (not on endpoints)
    if (t <= eps || t >= 1 - eps) continue;

    // Check if intersection point's angle is within the arc span
    const ix = fx + t * dx;
    const iy = fy + t * dy;
    let angle = Math.atan2(iy, ix);
    if (angle < 0) angle += TWO_PI;

    if (isAngleInArc(angle, startAngle, endAngle)) {
      return true;
    }
  }

  return false;
}

/** Check if `angle` falls within the arc from `start` to `end` (both in [0, 2π)). */
function isAngleInArc(angle: number, start: number, end: number): boolean {
  // Normalize all angles to [0, 2π)
  const a = ((angle % TWO_PI) + TWO_PI) % TWO_PI;
  const s = ((start % TWO_PI) + TWO_PI) % TWO_PI;
  const e = ((end % TWO_PI) + TWO_PI) % TWO_PI;

  if (e >= s) {
    // Simple range
    return a >= s && a <= e;
  }
  // Wraps around 0: e.g. start=350°, end=10° → covers 350°–360° and 0°–10°
  return a >= s || a <= e;
}

// ---------------------------------------------------------------------------
// Radial wall collision (spoke boundary)
// ---------------------------------------------------------------------------

function isPathCrossingRadialSpoke(
  from: CartesianCoord,
  to: CartesianCoord,
  wall: RadialWall,
): boolean {
  const { center, angle, innerRadius, outerRadius } = wall;

  // The spoke is a line segment from (center + innerRadius * dir) to (center + outerRadius * dir)
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  const sx = center.x + innerRadius * cosA;
  const sy = center.y + innerRadius * sinA;
  const ex = center.x + outerRadius * cosA;
  const ey = center.y + outerRadius * sinA;

  // Check segment-segment intersection between path (from→to) and spoke (s→e)
  return segmentsIntersect(from.x, from.y, to.x, to.y, sx, sy, ex, ey);
}

/**
 * Test whether two line segments (p1→p2) and (p3→p4) properly intersect
 * (crossing, not just touching at endpoints).
 */
function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number,
): boolean {
  const dx1 = x2 - x1;
  const dy1 = y2 - y1;
  const dx2 = x4 - x3;
  const dy2 = y4 - y3;

  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 1e-12) return false; // parallel or collinear

  const dx3 = x3 - x1;
  const dy3 = y3 - y1;

  const t = (dx3 * dy2 - dy3 * dx2) / denom;
  const u = (dx3 * dy1 - dy3 * dx1) / denom;

  const eps = 1e-9;
  return t > eps && t < 1 - eps && u > eps && u < 1 - eps;
}
